import { Grid } from "@mui/material"
import { ListContextProvider, ShowBase, useAuthState } from "ra-core"
import { FC } from "react"
import { Content } from "~/components/content/Content"
import { CreateBackupEntryTile, CreateAuditEntryTile, CreateDeploymentEntryTile, CreateEncryptionEntryTile, CreateSecureFileManagementTile } from "~/components/dashboard/entryTiles"
import DashboardAlertsCard from "~/components/dashboard/DashboardAlertsCard"
import DashboardStatusCard from "~/components/dashboard/DashboardStatusCard"
import DatabaseTable from "~/components/dashboard/DatabaseTable"
import { OdResourcePageProps } from "~/types/resource"
import LogViewer, { LogViewerProvider, useLogViewerController } from "~/components/backups/LogViewer"
import useLocalListController from "~/components/list/useLocalListController"
import nProgress from "nprogress"
import { Redirect } from "react-router-dom"
import { RecPatch } from "~/dataProvider/tools"
import { DatabaseResponseDto } from "~/gen/api/backup"
import { MANAGE_BACKUP_JOBS, MANAGE_DATABASE_DEPLOYMENTS, SCHEDULE_ENCRYPTION_OBFUSCATION } from "~/constants"
import { useHasPermit } from "~/authProvider/useHasPermit"
import { useSelector } from "react-redux"
import { OnDataFeature } from "~/gen/api/user"
import { getUserProfile } from "~/reducers/userProfile"


const Dashboard: FC<OdResourcePageProps> = (props) => {
  const { loading, authenticated } = useAuthState()
  const { permissions } = props
  const logCtrl = useLogViewerController()

  if (loading) {
    nProgress.start()
    return null
  }
  nProgress.done()
  if (!authenticated) {
    return <Redirect to="/login" />
  }
  
  return (
    <LogViewerProvider value={logCtrl}>
      <DashboardBody {...props} />
    </LogViewerProvider>
  )
}

const databaseListOptions : {sorters: Record<string, (a:DatabaseResponseDto & RecPatch, b:DatabaseResponseDto & RecPatch) => number>} = {
  sorters: {
    id: (a, b) => a.id < b.id ? -1 : 1,
    name: (a, b) => (a.name || '') < (b.name || '') ? -1 : 1,
    sizeText: (a, b) => (a.size || 0) < (b.size || 0) ? -1 : 1,
    lastBackupTime: (a, b) => (a.lastBackupTime || '') < (b.lastBackupTime || '') ? -1 : 1,
  }
}

export const DashboardBody: FC<OdResourcePageProps> = () => {
  const databaselistCtrl = useLocalListController({ resource: 'databases', basePath: '' }, databaseListOptions)
  const hasBackupPermit = useHasPermit([MANAGE_BACKUP_JOBS]),
    hasEncryptPermit = useHasPermit([SCHEDULE_ENCRYPTION_OBFUSCATION]),
    hasDeployPermit = useHasPermit([MANAGE_DATABASE_DEPLOYMENTS])
  const hasSecurefileFeature = useSelector(store => Boolean(getUserProfile(store)?.features?.some(f => f.featureId === OnDataFeature.OnDataSecureFile)))

  return (
    <ShowBase resource="backups-n-validations/dashboard" basePath="" id="-1" >
      <Content pageName="Dashboard" resourceName="dashboard">
        <Grid container spacing={4}>
          {(hasBackupPermit || hasEncryptPermit || hasDeployPermit) &&
            <Grid item xs={12} container spacing={4}>
              {hasBackupPermit &&
                <Grid item xs={12} sm={6} md={3}>
                  <CreateBackupEntryTile />
                </Grid>}
              {hasEncryptPermit &&
                <Grid item xs={12} sm={6} md={3}>
                  <CreateEncryptionEntryTile />
                </Grid>}
              {hasDeployPermit &&
                <Grid item xs={12} sm={6} md={3}>
                  <CreateDeploymentEntryTile />
                </Grid>}
              <Grid item xs={12} sm={6} md={3}>
                <CreateSecureFileManagementTile disabled={!hasSecurefileFeature} />
              </Grid>
            </Grid>}
          <Grid item xs={12} md={6}>
            <DashboardStatusCard />
          </Grid>
          <Grid item xs={12} md={6}>
            <DashboardAlertsCard />
          </Grid>
          <Grid item xs={12}>
            <ListContextProvider value={databaselistCtrl}>
              <DatabaseTable />
            </ListContextProvider>
          </Grid>
        </Grid>
        <LogViewer />
      </Content>
    </ShowBase>
  )
}

export default Dashboard
